import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, Clock, Download, Printer, Send, Undo2 } from 'lucide-react'
import athleteApi from '@/lib/api'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'

type InvoiceStatus = 'draft' | 'sent_to_athlete' | 'sent_to_brand' | 'paid' | 'void'

interface InvoiceLineItem {
  description: string
  quantity: number
  amount_cents: number
}

interface Invoice {
  id: string
  collaboration_id: string
  status: InvoiceStatus
  invoice_number: string
  line_items: InvoiceLineItem[]
  notes: string | null
  subtotal_cents: number
  total_cents: number
  currency: string
  recipient_email: string | null
  sent_to_athlete_at: string | null
  sent_to_brand_at: string | null
  paid_at: string | null
  presigned_pdf_url: string | null
  created_at: string
  updated_at: string
}

const formatCents = (cents: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 2,
  }).format(cents / 100)

const formatDate = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

const InvoiceReview = () => {
  const { id: collaborationId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [showSendModal, setShowSendModal] = useState(false)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [openingPdf, setOpeningPdf] = useState(false)

  const fetchInvoice = async () => {
    if (!collaborationId) return
    try {
      setLoading(true)
      const res = await athleteApi.get(`/athlete/invoices/collaborations/${collaborationId}`)
      setInvoice(res.data?.invoice ?? null)
    } catch (e: any) {
      toast(`Could not load invoice: ${e?.response?.data?.error || e?.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchInvoice()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaborationId])

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <p className="text-sm text-gray-400">Loading invoice...</p>
      </div>
    )
  }

  if (!invoice) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Link to="/earnings" className="text-gray-400 hover:text-white text-sm">
          ← Back to Earnings
        </Link>
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-gray-300">
          No invoice exists for this collaboration yet.
        </div>
      </div>
    )
  }

  const isPendingReview = invoice.status === 'sent_to_athlete'
  const isDraft = invoice.status === 'draft'
  const issueDate = new Date(invoice.created_at)
  const dueDate = new Date(issueDate.getTime() + 30 * 86400000)

  const handleApproveAndSend = async () => {
    if (!recipientEmail.trim() || !collaborationId) return
    try {
      setSubmitting(true)
      await athleteApi.post(
        `/athlete/invoices/collaborations/${collaborationId}/approve-and-send-to-brand`,
        { recipientEmail: recipientEmail.trim() },
      )
      toast(`Invoice approved and sent to ${recipientEmail}.`)
      setShowSendModal(false)
      await fetchInvoice()
    } catch (e: any) {
      toast(`Could not send: ${e?.response?.data?.error || e?.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  const handleSendDirect = async () => {
    if (!recipientEmail.trim() || !collaborationId) return
    try {
      setSubmitting(true)
      await athleteApi.post(`/athlete/invoices/collaborations/${collaborationId}/send-to-brand`, {
        recipientEmail: recipientEmail.trim(),
      })
      toast(`Invoice sent to ${recipientEmail}.`)
      setShowSendModal(false)
      await fetchInvoice()
    } catch (e: any) {
      toast(`Could not send: ${e?.response?.data?.error || e?.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  const handleOpenPdf = async () => {
    if (!collaborationId) return
    try {
      setOpeningPdf(true)
      const res = await athleteApi.get(`/athlete/invoices/collaborations/${collaborationId}/pdf`, {
        responseType: 'blob',
      })
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e: any) {
      toast(`Could not open PDF: ${e?.response?.data?.error || e?.message}`)
    } finally {
      setOpeningPdf(false)
    }
  }

  const handleReturn = async () => {
    if (!collaborationId) return
    try {
      setSubmitting(true)
      await athleteApi.post(
        `/athlete/invoices/collaborations/${collaborationId}/return-to-manager`,
      )
      toast('Returned to your manager for edits.')
      await fetchInvoice()
    } catch (e: any) {
      toast(`Could not return: ${e?.response?.data?.error || e?.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-5 flex items-center justify-between print:hidden">
        <button
          onClick={() => navigate('/earnings')}
          className="text-gray-400 hover:text-white inline-flex items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Earnings
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenPdf}
            disabled={openingPdf}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white border border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2 disabled:opacity-50"
            title="Opens a printable PDF in a new tab"
          >
            <Printer className="w-4 h-4" />
            {openingPdf ? 'Opening…' : 'View / Print PDF'}
          </button>
          {invoice.presigned_pdf_url && (
            <a
              href={invoice.presigned_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white border border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2"
            >
              <Download className="w-4 h-4" />
              View PDF
            </a>
          )}
        </div>
      </div>

      {isPendingReview && (
        <div className="mb-5 bg-blue-500/10 border border-blue-500/25 rounded-2xl px-6 py-4 print:hidden">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-blue-300" />
            </div>
            <div className="flex-1">
              <p className="text-white text-base font-semibold">Awaiting your review</p>
              <p className="text-sm text-blue-100/70 mt-0.5">
                Your manager prepared this invoice. Approve and send it to the brand, or return it
                with edits.
              </p>
            </div>
          </div>
        </div>
      )}

      {invoice.status === 'sent_to_brand' && (
        <div className="mb-5 bg-[#C0FD72]/10 border border-[#C0FD72]/30 rounded-2xl px-6 py-4 print:hidden">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#C0FD72]/20 border border-[#C0FD72]/30 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-[#C0FD72]" />
            </div>
            <div className="flex-1">
              <p className="text-white text-base font-semibold">
                Sent to {invoice.recipient_email || 'the brand'}
              </p>
              <p className="text-sm text-[#C0FD72]/80 mt-0.5">
                {invoice.sent_to_brand_at
                  ? `${new Date(invoice.sent_to_brand_at).toLocaleString()}. Awaiting payment per Net 30.`
                  : 'Awaiting payment.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {invoice.status === 'paid' && (
        <div className="mb-5 bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-2xl px-6 py-4 print:hidden">
          <div className="flex items-center gap-4">
            <CheckCircle2 className="w-6 h-6 text-[#4ade80]" />
            <p className="text-white font-semibold">
              Paid {invoice.paid_at ? `on ${new Date(invoice.paid_at).toLocaleDateString()}` : ''}
            </p>
          </div>
        </div>
      )}

      <InvoiceCard invoice={invoice} issueDate={issueDate} dueDate={dueDate} />

      {(isPendingReview || isDraft) && (
        <div className="space-y-3 print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              onClick={handleReturn}
              variant="outline"
              disabled={submitting || isDraft}
              className="border-white/15 text-white hover:bg-white/10 font-semibold h-14 text-base bg-white/5 disabled:opacity-50"
            >
              <Undo2 className="w-5 h-5 mr-2" />
              Return to manager
            </Button>
            <Button
              onClick={() => setShowSendModal(true)}
              disabled={submitting}
              className="bg-[#C0FD72] text-black hover:bg-[#C0FD72]/90 font-semibold h-14 text-base"
            >
              <Send className="w-5 h-5 mr-2" />
              {isPendingReview ? 'Approve and send to brand' : 'Send to brand'}
            </Button>
          </div>
          {isPendingReview && (
            <p className="text-sm text-gray-500 text-center">
              Returning sends it back to your manager for edits. Approving emails the PDF directly
              to the brand on your behalf.
            </p>
          )}
        </div>
      )}

      {showSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d1b24] p-6">
            <h2 className="text-lg font-semibold text-white mb-1">Send invoice to brand</h2>
            <p className="text-sm text-gray-400 mb-4">
              We'll email the PDF to this address with a body summarizing the invoice.
            </p>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Brand recipient email
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="ap@brand.com"
              className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-hidden focus:border-[#C0FD72]/40 mb-5"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowSendModal(false)}
                className="border-white/15 text-gray-300 hover:bg-white/10 bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={isPendingReview ? handleApproveAndSend : handleSendDirect}
                disabled={!recipientEmail.trim() || submitting}
                className="bg-[#C0FD72] text-black hover:bg-[#C0FD72]/90 font-semibold"
              >
                {submitting ? 'Sending…' : 'Send invoice'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const InvoiceCard = ({
  invoice,
  issueDate,
  dueDate,
}: {
  invoice: Invoice
  issueDate: Date
  dueDate: Date
}) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden mb-6 print:bg-white print:text-black print:border-0">
    <div className="px-8 pt-8 pb-6 border-b border-white/8 flex items-start justify-between print:border-gray-200">
      <div>
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-9 h-9 rounded-lg bg-[#C0FD72]/15 border border-[#C0FD72]/30 flex items-center justify-center">
            <span className="text-[#C0FD72] font-bold text-lg print:text-black">A</span>
          </div>
          <span className="text-white font-bold text-lg print:text-black">Athios</span>
        </div>
        <p className="text-3xl font-bold text-white print:text-black">Invoice</p>
        <p className="text-gray-400 text-sm mt-1 print:text-gray-600">{invoice.invoice_number}</p>
      </div>
      <div className="text-right text-sm text-gray-400 space-y-1 print:text-gray-700">
        <div>
          <span className="text-gray-500">Issued </span>
          <span className="text-white print:text-black">{formatDate(issueDate)}</span>
        </div>
        <div>
          <span className="text-gray-500">Due </span>
          <span className="text-white print:text-black">{formatDate(dueDate)}</span>
        </div>
        <div>
          <span className="text-gray-500">Terms </span>
          <span className="text-white print:text-black">Net 30</span>
        </div>
      </div>
    </div>

    <div className="px-8 py-6 border-b border-white/8 print:border-gray-200">
      <table className="w-full">
        <thead>
          <tr>
            <th className="text-left text-xs font-semibold tracking-wider text-gray-500 uppercase pb-3">
              Description
            </th>
            <th className="text-right text-xs font-semibold tracking-wider text-gray-500 uppercase pb-3 w-16">
              Qty
            </th>
            <th className="text-right text-xs font-semibold tracking-wider text-gray-500 uppercase pb-3 w-32">
              Amount
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5 print:divide-gray-200">
          {invoice.line_items.map((item, i) => (
            <tr key={i}>
              <td className="text-sm text-white py-2 print:text-black">{item.description}</td>
              <td className="text-sm text-gray-300 text-right py-2 print:text-black">
                {item.quantity}
              </td>
              <td className="text-base font-semibold text-white text-right py-2 print:text-black">
                {formatCents(item.amount_cents, invoice.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="px-8 py-6 border-b border-white/8 print:border-gray-200">
      <div className="flex justify-end">
        <div className="w-full max-w-xs space-y-2.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400 print:text-gray-700">Subtotal</span>
            <span className="text-white print:text-black">
              {formatCents(invoice.subtotal_cents, invoice.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between pt-2.5 border-t border-white/8 print:border-gray-200">
            <span className="text-base font-semibold text-white print:text-black">Total Due</span>
            <span className="text-2xl font-bold text-[#C0FD72] print:text-black">
              {formatCents(invoice.total_cents, invoice.currency)}
            </span>
          </div>
        </div>
      </div>
    </div>

    {invoice.notes && (
      <div className="px-8 py-6 print:bg-white">
        <h3 className="text-xs font-semibold tracking-wider text-gray-500 uppercase mb-2">
          Notes
        </h3>
        <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap print:text-black">
          {invoice.notes}
        </p>
      </div>
    )}
  </div>
)

export default InvoiceReview
